import { Component, Output, EventEmitter } from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { Router } from '@angular/router';
import { secureStorage } from '../../shared/services/secure-storage.service';
import { CookieService } from '../../shared/services/cookie.service';
import { RootService } from '../../shared/services/root.service';
import { TokenService } from '../../shared/services/token.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CartService } from '../../shared/services/cart.service';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {
  @Output() registrationSuccess = new EventEmitter<void>();
  personalDetails: PersonalDetails = {
    firstName: '',
    lastName: '',
    email: '',
  };

  userTypes: UserType[] = USER_TYPES;
  selectedUserType: UserType | null = null;
  loading = false;
  errorMessage = '';
  private sub: Subscription = new Subscription();

  // First Name filtering properties
  showFirstNameSuggestions = false;
  firstNameSuggestions: string[] = [];
  filteredFirstNameSuggestions: string[] = [];

  // Common first names for suggestions


  constructor(
    private authService: AuthService,
    private router: Router,
    private localstorage: secureStorage,
    private storageService: RootService,
    private ssrcookieService: CookieService,
    private tokenService: TokenService,
    private cartService: CartService
  ) {}

  isFormValid(): boolean {
    return (
      !!this.personalDetails.firstName?.trim() &&
      !!this.personalDetails.lastName?.trim() &&
      this.isNameValid(this.personalDetails.firstName) &&
      this.isNameValid(this.personalDetails.lastName) &&
      this.isEmailValid(this.personalDetails.email) &&
      !!this.selectedUserType
    );
  }

  onSubmit(): void {
    // Mark all fields as touched to show validation errors
    this.firstNameTouched = true;
    this.lastNameTouched = true;
    this.emailTouched = true;
    this.userTypeTouched = true;

    if (this.isFormValid() && !this.loading) {
      this.loading = true;
      this.errorMessage = '';

      // Get phone number and OTP from the parent component's flow
      const phoneNumber = localStorage.getItem('registrationPhoneNumber');
      const otpNumber = localStorage.getItem('registrationOtp');

      if (!phoneNumber || !otpNumber) {
        this.errorMessage = 'Missing phone number or OTP. Please try again.';
        this.loading = false;
        return;
      }

      const registrationData = {
        phone_number: phoneNumber,
        first_name: this.personalDetails.firstName.trim(),
        last_name: this.personalDetails.lastName.trim(),
        email_id: this.personalDetails.email?.trim() || '',
        // otp_number: otpNumber,
        user_type: this.selectedUserType?.label || '',
      };

      this.sub.add(
        this.authService.register(registrationData).subscribe({
          next: (response) => {
            this.loading = false;

            // Store tokens from registration response first
            this.tokenService.storeAuthData({
              access_token: response?.data?.token['access_token'],
              refresh_token: response?.data?.token['refresh_token'],
              user_code: response?.data?.user_code,
              expires_in: response?.data?.token['expires_in'],
            });

            // Store additional user data
            this.ssrcookieService.setItem(
              'eol_user',
              JSON.stringify(response.data)
            );
            this.localstorage.setItem(
              'eol_user',
              JSON.stringify(response.data)
            );

            // Clear temporary storage
            localStorage.removeItem('registrationPhoneNumber');
            localStorage.removeItem('registrationOtp');

            // Use auth service to update login state
            this.authService.login(response.data);

            // Force refresh auth state to ensure components get updated state
            setTimeout(() => {
              this.authService.refreshAuthState();
            }, 100);

            this.syncLocalStorageCart();
            this.registrationSuccess.emit();
            // this.router.navigate(['/home']);
          },
          error: (error) => {
            this.loading = false;
            this.errorMessage =
              error.message || 'Registration failed. Please try again.';
          },
        })
      );
    }
  }

  private syncLocalStorageCart(): void {
    if (this.cartService && this.cartService.hasLocalStorageItems()) {
      this.cartService.syncLocalStorageToServer().subscribe({
        next: (res: any) => {
          console.log('Successfully synced local storage cart items:', res);
        },
        error: (err: any) => {
          console.error('Error syncing local storage cart items:', err);
        },
      });
    }
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  onClose(): void {
    this.registrationSuccess.emit();
    this.router.navigate(['/home']);
  }

  // Validation methods
  validateNameInput(event: any, field: 'firstName' | 'lastName'): void {
    const input = event.target;
    const value = input.value;

    // Remove any non-letter characters (only allow letters)
    const filteredValue = value.replace(/[^a-zA-Z]/g, '');

    // Update the model with filtered value
    if (field === 'firstName') {
      this.personalDetails.firstName = filteredValue;
    } else {
      this.personalDetails.lastName = filteredValue;
    }

    // Update the input value
    input.value = filteredValue;
  }

  isNameValid(name: string): boolean {
    return /^[a-zA-Z]+$/.test(name);
  }

  isEmailValid(email: string | undefined): boolean {
    // Email is optional, so if it's empty or undefined, it's valid
    if (!email || email.trim() === '') {
      return true;
    }
    // If email is provided, validate the format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  getFirstNameError(): string {
    if (!this.firstNameTouched) return '';
    if (!this.personalDetails.firstName) {
      return 'First name is required';
    }
    if (!this.isNameValid(this.personalDetails.firstName)) {
      return 'First name should contain only letters';
    }
    return '';
  }

  getLastNameError(): string {
    if (!this.lastNameTouched) return '';
    if (!this.personalDetails.lastName) {
      return 'Last name is required';
    }
    if (!this.isNameValid(this.personalDetails.lastName)) {
      return 'Last name should contain only letters';
    }
    return '';
  }

  getEmailError(): string {
    if (!this.emailTouched) return '';
    if (this.personalDetails.email && !this.isEmailValid(this.personalDetails.email)) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  getUserTypeError(): string {
    if (!this.userTypeTouched) return '';
    if (!this.selectedUserType) {
      return 'Please select a user type';
    }
    return '';
  }

  // Track field interactions
  firstNameTouched = false;
  lastNameTouched = false;
  emailTouched = false;
  userTypeTouched = false;

  onFirstNameBlur(): void {
    this.firstNameTouched = true;
  }

  onLastNameBlur(): void {
    this.lastNameTouched = true;
  }

  onEmailBlur(): void {
    this.emailTouched = true;
  }

  onUserTypeSelect(type: UserType): void {
    this.userTypes.forEach((t) => (t.selected = false)); // Unselect all
    type.selected = true;
    this.selectedUserType = type;
    this.userTypeTouched = true;
  }
}

export interface UserType {
  label: string;
  selected: boolean;
}

export const USER_TYPES: UserType[] = [
  { label: 'Individual', selected: false },
  { label: 'Fleet owner', selected: false },
  { label: 'Garage Owner', selected: false },
  { label: 'Mechanic', selected: false },
  { label: 'Retailer', selected: false },
  { label: 'End Product Manufacturer', selected: false },
  { label: 'Scrap Recycler', selected: false },
  { label: 'Scrap Trader', selected: false },
  { label: 'Refurbisher', selected: false },
];

export type UserTypeLabel =
  | 'Individual'
  | 'Fleet owner'
  | 'Garage Owner'
  | 'Mechanic'
  | 'Retailer'
  | 'End Product Manufacturer'
  | 'Scrap Recycler'
  | 'Scrap Trader'
  | 'Refurbisher';

export interface PersonalDetails {
  firstName: string;
  lastName: string;
  email?: string;
}
